/// <amd-module name="Repos/Discussion/Components/UpdateComment" />
import * as React from "react";
import { DiscussionStatus } from "Repos/Discussion/Common/DiscussionContracts";
import * as Resources from "Repos/Discussion/Resources";
import { ScreenContext, ScreenSize } from "VSS/Core/Util/Screen";
import { Button } from "VSSUI/Button";
import { Observer } from "VSSUI/Observer";
import { SplitButton } from "VSSUI/SplitButton";
import { isActiveCommentStatus } from "../Utils/DiscussionStatus";
import { EditComment } from "./EditComment";
import { IUpdateCommentProps } from "./UpdateComment.Props";

export const UpdateComment = (props: IUpdateCommentProps): JSX.Element => {
    const { author, className, comment, markdownEditorProps, onUpdateComment, personaProvider, status, onCancel } = props;

    const submitChanges = (status?: DiscussionStatus): void => {
        const content = comment.getContent().value;
        content && onUpdateComment && onUpdateComment(comment.getId(), content, status);
    };

    const cancelChanges = (): void => {
        comment.revertComment();
        props.onCancel?.();
    };
    const buttons = (
        <ScreenContext.Consumer>
            {screen => (
                <Observer
                    dirty={comment.isDirty()}
                    hasContent={comment.hasContent()}
                    screenSize={screen.size}
                    status={status}
                    saving={comment.isSaving()}
                >
                    {(buttonProps: { dirty: boolean; hasContent: boolean; screenSize: ScreenSize; status: DiscussionStatus; saving: boolean }) => {
                        const updateDisabled = !buttonProps.hasContent || !buttonProps.dirty || buttonProps.saving;

                        if (buttonProps.screenSize <= ScreenSize.xsmall) {
                            return (
                                <>
                                    <Button disabled={buttonProps.saving} onClick={cancelChanges}>
                                        {Resources.CancelButtonText}
                                    </Button>
                                    <SplitButton
                                        buttonProps={{
                                            onClick: () => submitChanges(),
                                            text: Resources.UpdateButtonText
                                        }}
                                        disabled={updateDisabled}
                                        menuButtonProps={{
                                            contextualMenuProps: {
                                                menuProps: {
                                                    id: "update-split-button-menu",
                                                    items: [
                                                        {
                                                            id: "update-menu-item",
                                                            onActivate: () => submitChanges(),
                                                            text: Resources.UpdateButtonText
                                                        },
                                                        {
                                                            id: "update-resolve-menu-item",
                                                            onActivate: () =>
                                                                submitChanges(
                                                                    isActiveCommentStatus(buttonProps.status)
                                                                        ? DiscussionStatus.Fixed
                                                                        : DiscussionStatus.Active
                                                                ),
                                                            text: isActiveCommentStatus(buttonProps.status)
                                                                ? Resources.UpdateAndResolveButtonText
                                                                : Resources.UpdateAndReactivateButtonText
                                                        }
                                                    ]
                                                }
                                            }
                                        }}
                                        primary={true}
                                    />
                                </>
                            );
                        }

                        return (
                            <>
                                <Button disabled={buttonProps.saving} onClick={cancelChanges}>
                                    {Resources.CancelButtonText}
                                </Button>
                                <Button
                                    disabled={updateDisabled}
                                    onClick={() =>
                                        submitChanges(isActiveCommentStatus(buttonProps.status) ? DiscussionStatus.Fixed : DiscussionStatus.Active)
                                    }
                                >
                                    {isActiveCommentStatus(buttonProps.status)
                                        ? Resources.UpdateAndResolveButtonText
                                        : Resources.UpdateAndReactivateButtonText}
                                </Button>
                                <Button disabled={updateDisabled} onClick={() => submitChanges()} primary={true}>
                                    {Resources.UpdateButtonText}
                                </Button>
                            </>
                        );
                    }}
                </Observer>
            )}
        </ScreenContext.Consumer>
    );

    return (
        <EditComment
            author={author}
            buttons={buttons}
            className={className}
            comment={comment.getContent()}
            dirty={comment.isDirty()}
            error={comment.getError()}
            markdownEditorProps={markdownEditorProps}
            onCancel={cancelChanges}
            onChange={newComment => comment.setContent(newComment)}
            onError={error => comment.setError(error)}
            onSubmit={submitChanges}
            personaClassName="margin-top-4"
            personaProvider={personaProvider}
            saving={comment.isSaving()}
        />
    );
};
